import { useState, useEffect } from "react";
import { FaUser, FaEnvelope, FaCreditCard, FaLock, FaCalendarAlt, FaHistory, FaClock } from "react-icons/fa";
import { useAuth } from "../auth/AuthContext"; 
import LoadingIndicator from "../components/LoadingIndicator";
import { getPlanDetails } from "../config/subPlans";
import ChangePassword from "../components/ChangePassword";

const Account = () => {
  const { user } = useAuth(); // Get the user data from auth context
  const [loading, setLoading] = useState(true);
  // const [editedName, setEditedName] = useState(""); // Currently unused
  // const [isEditing, setIsEditing] = useState(false); // Currently unused
  const [planHistory, setPlanHistory] = useState([]);
  const [showChangePassword, setShowChangePassword] = useState(false);
  
  // Set the edited name from the user data when it's available
  useEffect(() => {
    setLoading(true);
    
    if (user && user.name) {
      // setEditedName(user.name); // Currently unused
      
      // Parse plan history if available
      if (user['custom:planHistory'] && user['custom:planHistory'] !== 'na') {
        try {
          const history = JSON.parse(user['custom:planHistory']);
          setPlanHistory(Array.isArray(history) ? history : []);
        } catch (error) {
          console.error("Error parsing plan history:", error);
          setPlanHistory([]);
        }
      }
    }
    
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [user]);

  // const handleEdit = () => {
  //   setIsEditing(true);
  // };

  // const handleSave = () => {
  //   // Here you would typically call an API to update the user's name
  //   // For example: updateUserProfile({ name: editedName })
  //   setIsEditing(false);
  // };

  // const handleCancel = () => {
  //   setEditedName(user?.name || "");
  //   setIsEditing(false);
  // };

  const handleChangePasswordClick = () => {
    setShowChangePassword(true);
  };

  const handleClosePasswordModal = () => {
    setShowChangePassword(false);
  };

  // Format date from ISO to display format (YYYY-MM-DD)
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'na' || dateString === 'NA') return "Not available";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  return (
    // Following top-down UI architecture:
    // - w-full h-full ensures it fills available space
    // - overflow-auto handles content overflow without breaking layout
    // - No min-height, custom width or layout overrides
    <div className="w-full h-full overflow-auto">
      {loading && <LoadingIndicator />}
      <div className="px-6 py-6 transition-all duration-300 min-h-screen"
   style={{
    paddingTop: 'calc(var(--navbar-height) + 1.5rem)', // Adds navbar height + existing py-6 (1.5rem)
    backgroundColor: 'transparent',
    color: 'var(--text-primary)'
  }}>
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Account Settings</h1>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Manage your personal information and subscription</p>
        {!loading && user ? (
          <>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Profile Information Card */}
              <div className="p-6 rounded-lg" 
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  boxShadow: 'var(--shadow-box)'
                }}>
                <h2 className="text-xl font-semibold flex items-center" style={{ color: 'var(--text-primary)' }}>
                  <FaUser className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                  <span>Profile Information</span>
                </h2>
                
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Full Name</label>
                    <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{user.name}</div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Email Address</label>
                    <div className="flex items-center">
                      <FaEnvelope className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                      <div style={{ color: 'var(--text-primary)' }}>{user.email}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Member Since</label>
                    <div style={{ color: 'var(--text-primary)' }}>{formatDate(user['custom:registrationDate'])}</div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Account Status</label>
                    <div className="flex items-center">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${user['custom:accountStatus'] === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      <div className="capitalize" style={{ color: 'var(--text-primary)' }}>{user['custom:accountStatus']}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Membership Details Card */}
              <div className="p-6 rounded-lg" 
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  boxShadow: 'var(--shadow-box)'
                }}>
                <h2 className="text-xl font-semibold flex items-center" style={{ color: 'var(--text-primary)' }}>
                  <FaCreditCard className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                  <span>Membership Details</span>
                </h2>
                
                <div className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Current Plan</label>
                    <div className="text-green-600 font-semibold text-lg">
                      {getPlanDetails(user['custom:membership']).name}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>AI Usage</label>
                    <div style={{ color: 'var(--text-primary)' }}>
                      {user['custom:messagesUsed'] || 0} / {user['custom:limit'] || getPlanDetails(user['custom:membership']).messageLimit} messages
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Reset Date</label>
                    <div className="flex items-center" style={{ color: 'var(--text-primary)' }}>
                      <FaCalendarAlt className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                      {formatDate(user['custom:resetDate'])}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Next Billing Date</label>
                    <div className="flex items-center" style={{ color: 'var(--text-primary)' }}>
                      <FaCalendarAlt className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                      {formatDate(user['custom:nextBillingDate'])}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Payment Method</label>
                    <div className="flex items-center" style={{ color: 'var(--text-primary)' }}>
                      <FaCreditCard className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                      {user['custom:paymentMethod']}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Last Payment</label>
                    <div className="flex items-center" style={{ color: 'var(--text-primary)' }}>
                      <FaClock className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                      {formatDate(user['custom:lastPaymentDate'])}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm block" style={{ color: 'var(--text-secondary)' }}>Billing Cycle</label>
                    <div style={{ color: 'var(--text-primary)' }}>{user['custom:billingCycle']}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Plan History Card (Only shown if history exists) */}
            {planHistory.length > 0 && (
              <div className="mt-6 p-6 rounded-lg" 
                style={{ 
                  backgroundColor: 'var(--bg-secondary)', 
                  boxShadow: 'var(--shadow-box)'
                }}>
                <h2 className="text-xl font-semibold flex items-center" style={{ color: 'var(--text-primary)' }}>
                  <FaHistory className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                  <span>Plan History</span>
                </h2>
                
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y" style={{ borderColor: 'var(--text-secondary)' }}>
                    <thead>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Change</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: 'var(--text-secondary)' }}>
                      {planHistory.map((change, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {formatDate(change.date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                            Changed from <span className="font-semibold">{getPlanDetails(change.from).name}</span> to <span className="font-semibold">{getPlanDetails(change.to).name}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Security Settings Card */}
            <div className="mt-6 p-6 rounded-lg" 
              style={{ 
                backgroundColor: 'var(--bg-secondary)', 
                boxShadow: 'var(--shadow-box)'
              }}>
              <h2 className="text-xl font-semibold flex items-center" style={{ color: 'var(--text-primary)' }}>
                <FaLock className="mr-2" style={{ color: 'var(--text-secondary)' }} />
                <span>Security Settings</span>
              </h2>
              
              <div className="mt-4 space-y-4">
                <div className="flex justify-between items-center py-3 border-b" style={{ borderColor: 'var(--text-secondary)' }}>
                  <div>
                    <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>Password</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Regularly changing your password improves security</p>
                  </div>
                  <button 
                    className="text-blue-600 hover:text-blue-800 transition"
                    onClick={handleChangePasswordClick}
                  >
                    Change
                  </button>
                </div>
                
                {/* <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <div>
                    <h3 className="font-medium">Two-Factor Authentication</h3>
                    <p className="text-sm text-gray-500">Enhance your account security</p>
                  </div>
                  <button className="text-blue-600 hover:text-blue-800 transition">
                    Enable
                  </button>
                </div> */}
                
                {/* <div className="flex justify-between items-center py-3">
                  <div>
                    <h3 className="font-medium">Session Management</h3>
                    <p className="text-sm text-gray-500">Manage active sessions</p>
                  </div>
                  <button className="text-blue-600 hover:text-blue-800 transition">
                    View
                  </button>
                </div> */}
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="max-w-md w-full mx-4">
            <ChangePassword onClose={handleClosePasswordModal} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;